<template>
  <div>
    <div id="content-area">
      <div class="content-wrapper">
        <div class="container">
          <back-to-top bottom="5%" right="30px" visibleoffset="400">
            <vs-button icon-pack="feather" icon="icon-arrow-up" class="shadow-lg btn-back-to-top" />
          </back-to-top>
          <div class="content-area__content ds-wrapper">
            <div class="sds-top-card flex items-center justify-center mt-8">
              <img :src="nationwideIcon" alt="Nationwide Conveyancing" :class="logo ? 'logo-small' : ''" />
              <plus-icon size="4x" class="button-icon text-dark md:mx-8" :class="logo ? 'mx-2' : 'mx-4'"></plus-icon>
              <img :src="relloLogo" alt="Rello" :class="logo ? 'logo-small' : ''" />
              <plus-icon size="4x" class="button-icon text-dark mx-2 md:mx-8" v-if="logo"></plus-icon>
              <img :src="logo" alt="logo" v-if="logo" class="logo-small" />
            </div>
            <div class="relative md:w-8/12 md:max-w-2lg md:mx-auto" v-if="showSuccessBox">
              <div class="success-message text-center mt-32 bg-success-200-transparent pt-10 px-4 pb-8 sm:pt-14 sm:px-6 sm:pb-10 rounded-2xl sm:rounded-3xl`">
                <div class="check-box bg-success-500 h-16 w-16 sm:h-18 sm:w-18 flex justify-center items-center rounded-full absolute">
                  <vs-icon type="flat" icon="done" class="text-shade-white" icon-size="large"></vs-icon>
                </div>
                <h2 class="mb-2 text-shade-black">All done!</h2>
                <p class="mb-4 text-body-dark" v-if="vendorOnly">
                  Your order has been created, we'll start work as soon as the invoice is paid. Check your email/mobile for a message from Rello requesting payment, follow the link provided
                  and make payment via your preferred method - then expect a specialist from our team to contact you to get started!
                </p>
                <p class="mb-4 text-body-dark" v-else>Your order has been created, we'll start work as soon as the invoice is paid.</p>
                <p class="mb-4 text-body-dark">
                  If you've any further queries, please contact <span v-if="vendorOnly">Rello's</span><span v-else>our</span> support team on 1300 815 923 or <a href="mailto:support@rellopay.co">support@rellopay.co</a>, and quote the reference
                  "<b>{{ paymentRequest.sdsOrderCustomId }}</b>" (this is also noted in the <span v-if="vendorOnly">message</span><span v-else>notification</span> sent to you). You can also check the Rello
                  <a href="https://help.rellopay.co/en/collections/65006-conveyancing" target="_blank">help centre</a> for common queries.
                </p>
                <p class="mb-4 text-body-dark">Download <span v-if="vendorOnly">your</span> tax invoice:</p>
                <S3FileGenerator
                  :document="paymentRequest.sdsTaxInvoice"
                  :key="paymentRequest.sdsTaxInvoice"
                  :custom-class="'block font-normal text-base'"
                />
              </div>
            </div>
            <div class="main-content mt-8" v-if="dataLoaded && !showSuccessBox">
              <div class="mb-8">
                <h2 class="mb-1 text-center">{{ sdsSettings.header }}</h2>
                <p class="text-center" v-if="vendorOnly" v-html="sdsSettings.descriptionForDirectOrderVendor"></p>
                <p class="text-center" v-else v-html="sdsSettings.descriptionForDirectOrder"></p>
              </div>
              <!--------- Agency details --------->
              <p class="text-body-dark mb-4">Agency details</p>
              <div class="grid gap-y-8 md:grid-cols-3 md:gap-x-10 mb-10">
                <div>
                  <vs-input
                    label="Contact name"
                    name="agencyName"
                    placeholder="Required"
                    v-model.trim="agencyName"
                    v-validate="'required'"
                    data-vv-as="name"
                    autocomplete="new-password"
                    :danger="errors.has('agencyName')" />
                  <span class="text-danger text-sm" v-show="errors.has('agencyName')">{{ errors.first("agencyName") }}</span>
                </div>
                <div>
                  <vs-input
                    label="Email"
                    name="agencyEmail"
                    v-model.trim="agencyEmail"
                    v-validate="'required|email'"
                    placeholder="Required"
                    data-vv-as="email"
                    autocomplete="new-password"
                    @change="setNotificationEmail"
                    :danger="errors.has('agencyEmail')" />
                  <div v-if="!vendorOnly">
                    <vs-checkbox class="mt-1" name="sdsTerms" id="sdsTerms" v-model="emailNotify">Use this email for notifications about this order</vs-checkbox>
                  </div>
                  <span class="text-danger text-sm" v-show="errors.has('agencyEmail')">{{ errors.first("agencyEmail") }}</span>
                </div>
                <div :class="errors.has('agencyPhone') ? 'input-icon-validate-danger' : ''" class="input-datepicker">
                  <label for="phone" :class="errors.has('agencyPhone') ? 'danger' : ''" class="input-label">Phone number</label>
                  <the-mask
                    class="vs-input--input w-full datepicker-input mask-input phone-w"
                    id="agencyPhone"
                    ref="agencyPhone"
                    v-model="agencyPhone"
                    masked
                    placeholder="Required"
                    name="agencyPhone"
                    data-vv-as="phone number"
                    v-validate="'required|phoneFormat'"
                    :mask="[checkPhoneNumber(agencyPhone)]"
                    :danger="errors.has('agencyPhone')"
                  />
                  <span class="text-danger text-sm" v-show="errors.has('agencyPhone')">{{ errors.first("agencyPhone") }}</span>
                </div>
              </div>

              <!--------- Notification email --------->
              <div v-show="!emailNotify && !vendorOnly">
                <p class="text-body-dark mb-4">Agency notifications</p>
                <div class="grid gap-y-8 md:grid-cols-3 md:gap-x-10 mb-10">
                  <div>
                    <vs-input
                      label="Email"
                      name="notificationEmail"
                      v-model.trim="agencyNotificationEmail"
                      v-validate="(!emailNotify && !vendorOnly) ? 'required|email' : ''"
                      placeholder="Required"
                      data-vv-as="email"
                      autocomplete="new-password"
                      :danger="errors.has('notificationEmail')" />
                    <span class="text-danger text-sm" v-show="errors.has('notificationEmail')">{{ errors.first("notificationEmail") }}</span>
                  </div>
                </div>
              </div>

              <!--------- Customer details --------->
              <p class="text-body-dark mb-4">Vendor details</p>
              <div class="grid gap-y-8 md:grid-cols-3 md:gap-x-10 mb-10">
                <div>
                  <vs-input
                    label="Name"
                    name="vendorName"
                    placeholder="Required"
                    v-model.trim="vendorName"
                    v-validate="'required'"
                    autocomplete="new-password"
                    data-vv-as="name"
                    :danger="errors.has('vendorName')" />
                  <span class="text-danger text-sm" v-show="errors.has('vendorName')">{{ errors.first("vendorName") }}</span>
                </div>
                <div>
                  <vs-input
                    label="Email"
                    name="vendorEmail"
                    v-model.trim="vendorEmail"
                    v-validate="'required|email'"
                    placeholder="Required"
                    autocomplete="new-password"
                    data-vv-as="email"
                    :danger="errors.has('vendorEmail')" />
                  <span class="text-danger text-sm" v-show="errors.has('vendorEmail')">{{ errors.first("vendorEmail") }}</span>
                </div>
                <div :class="errors.has('vendorPhone') ? 'input-icon-validate-danger' : ''" class="input-datepicker">
                  <label for="mobile" :class="errors.has('vendorPhone') ? 'danger' : ''" class="input-label">Phone number</label>
                  <the-mask
                    class="vs-input--input w-full datepicker-input mask-input phone-w"
                    id="vendorPhone"
                    ref="vendorPhone"
                    v-model="vendorPhone"
                    masked
                    placeholder="Required"
                    name="vendorPhone"
                    data-vv-as="phone number"
                    v-validate="'required|phoneFormat'"
                    :mask="[checkPhoneNumber(vendorPhone)]"
                    :danger="errors.has('vendorPhone')"
                  />
                  <span class="text-danger text-sm" v-show="errors.has('vendorPhone')">{{ errors.first("vendorPhone") }}</span>
                </div>
              </div>

              <p class="text-body-dark mb-4">Property address</p>
              <div class="grid gap-y-6 mb-10">
                <!--------- Sds address google autocomplete --------->
                <div>
                  <div v-if="!isAddressExpanded" class="custom-input-field">
                    <vue-google-autocomplete
                      ref="sdsAddressRef"
                      v-on:placechanged="getAddressData"
                      id="map"
                      classname="w-full custom-w sds-address"
                      :country="['au']"
                      placeholder="Start typing your address"
                      @change="addressChange"
                      @keydown="addressChange"
                      @focus="setAttributeAutocomplete"
                    ></vue-google-autocomplete>
                  </div>
                  <div v-show="errors.has('sdsAddress')">
                    <span class="text-danger text-sm">{{ errors.first("sdsAddress") }}</span>
                  </div>
                  <vs-button type="flat" class="mt-2" @click="toggleExpand" v-show="isAddressExpanded" size="small">Back to auto-complete</vs-button>
                  <vs-button type="flat" class="mt-2" @click="toggleExpand" v-show="!isAddressExpanded" size="small">Enter manually</vs-button>
                </div>
                <!--------- Sds address manual entry --------->
                <div v-if="isAddressExpanded">
                  <div class="grid gap-y-6">
                    <div>
                      <vs-input label="Unit" v-model.trim="sdsAddress.unit_number" @change="updateAddress('unit_number')" autocomplete="new-password" />
                    </div>
                    <div>
                      <vs-input
                        label="Street address"
                        v-model.trim="sdsAddress.street_name"
                        name="street_name"
                        v-validate="'required'"
                        @change="updateAddress('street_name')"
                        autocomplete="new-password"
                        data-vv-as="street name" />
                      <span class="text-danger text-sm" v-show="errors.has('street_name')">{{ errors.first("street_name") }}</span>
                    </div>
                    <div>
                      <vs-input label="Suburb" v-model.trim="sdsAddress.suburb" name="suburb" v-validate="'required'" @change="updateAddress('suburb')" autocomplete="new-password" />
                      <span class="text-danger text-sm" v-show="errors.has('suburb')">{{ errors.first("suburb") }}</span>
                    </div>
                    <div class="flex flex-row gap-x-6">
                      <div class="w-1/2">
                        <label class="input-label">State</label>
                        <vs-select v-model.trim="sdsAddress.state" class="custom-w full-width-select rounded-none" name="state" key="state" v-validate="'required'" @change="updateAddress('state')">
                          <vs-select-item :key="index" :value="item.value" :text="item.text" v-for="(item, index) in states" placeholder="Select state"></vs-select-item>
                        </vs-select>
                        <span class="text-danger text-sm" v-show="errors.has('state')">{{ errors.first("state") }}</span>
                      </div>
                      <div class="w-1/2">
                        <vs-input
                          label="Postcode"
                          maxlength="4"
                          v-model.trim="sdsAddress.postcode"
                          name="postcode"
                          v-validate="'required'"
                          @change="updateAddress('postcode')"
                          onkeypress="return /[0-9]/i.test(event.key)"
                          autocomplete="new-password" />
                        <span class="text-danger text-sm" v-show="errors.has('postcode')">{{ errors.first("postcode") }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!--------- Sales authority file upload --------->
              <p class="text-body-dark mb-4">Form 6 / Sales authority</p>
              <div class="mb-10" v-if="showFilePreview">
                <div class="flex flex-row space-between">
                  <a class="mr-5">{{ sdsSalesAuthority.name }}</a>
                  <img class="icon-control pointer" :src="deleteicon" alt="file delete" @click="clearFilePreview">
                </div>
                <div class="text-danger text-sm" v-show="errors.has('salesAuthorityFile')">
                  {{ errors.first("salesAuthorityFile") }}
                </div>
              </div>
              <div class="mb-10" v-show="!showFilePreview">
                <input
                  type="file"
                  class="hidden"
                  v-validate="{ required: fileLoadedFromUrl ? false : true, size: maxFileSize * 1024 }"
                  name="salesAuthorityFile"
                  accept=".pdf"
                  ref="sdsSalesAuthorityRef"
                  data-vv-as="sales authority file"
                  @change="updateFile($event)" />
                <div class="input-file">
                  <vs-button
                    type="flat"
                    icon="icon-upload"
                    icon-pack="feather"
                    icon-size="small"
                    @click="$refs.sdsSalesAuthorityRef.click()"
                  >
                    Upload file
                  </vs-button>
                </div>
                <div class="text-danger text-sm" v-show="errors.has('salesAuthorityFile')">
                  {{ errors.first("salesAuthorityFile") }}
                </div>
              </div>

              <!--------- Sds options --------->
              <div class="md:w-4/5">
                <seller-disclosure-statement :display-header="false" :display-type="'directOrder'" @setSdsSettings="setSdsSettings" @updateSdsDetails="setSdsDetails" ref="sellerDisclosureStatementFields" />
              </div>
              <div v-show="errors.has('sdsOrderItems')">
                <span class="text-danger text-sm">{{ errors.first("sdsOrderItems") }}</span>
              </div>

              <div class="flex justify-center mt-14">
                <vs-button color="primary" size="large" class="w-full max-w-400" @click="createPr" :disabled="disableCreateButton">
                  Order services <span class="ml-2" v-if="totalAmount > 0">({{ formatCurrency(totalAmount) }})</span>
                </vs-button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <the-footer />
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { PlusIcon } from "vue-feather-icons";
import { TheMask } from "vue-the-mask";

import BackToTop from "vue-backtotop";
import SellerDisclosureStatement from "./pages/partners/pages/paymentRequests/SellerDisclosureStatement.vue";
import S3FileGenerator from "./components/S3FileGenerator.vue";
import TheFooter from "@/layouts/guest-components/TheFooter.vue";
import VueGoogleAutocomplete from "vue-google-autocomplete";

export default {
  components: {
    BackToTop,
    PlusIcon,
    SellerDisclosureStatement,
    S3FileGenerator,
    TheFooter,
    TheMask,
    VueGoogleAutocomplete
  },

  data() {
    return {
      agencyEmail: "",
      agencyName: "",
      agencyNotificationEmail: "",
      agencyPhone: "",
      dataLoaded: false,
      deleteicon: require("@/assets/images/delete.svg"),
      emailNotify: false,
      file: "",
      fileLoadedFromUrl: false,
      hasCookie: false,
      isAddressExpanded: false,
      logo: "",
      maxFileSize: process.env.VUE_APP_FILE_MAX_SIZE_MB,
      merchantId: "",
      nationwideIcon: require("@/assets/images/nationwide.svg"),
      paymentRequest: {},
      relloLogo: require("@/assets/images/logo/rello-logo.svg"),
      sdsAddress: {
        country: "",
        postcode: "",
        rawAddress: "",
        state: "",
        street_name: "",
        street_number: "",
        suburb: "",
        unit_number: "",
      },
      sdsOrderItems: [],
      sdsSalesAuthority: "",
      sdsSettings: {},
      showSuccessBox: false,
      source: "",
      states: [
        { text: "NSW", value: "NSW" },
        { text: "ACT", value: "ACT" },
        { text: "QLD", value: "QLD" },
        { text: "VIC", value: "VIC" },
        { text: "NT", value: "NT" },
        { text: "SA", value: "SA" },
        { text: "WA", value: "WA" },
        { text: "TAS", value: "TAS" },
      ],
      totalAmount: 0,
      vendorEmail: "",
      vendorName: "",
      vendorOnly: false,
      vendorPhone: "",
    }
  },

  async mounted() {
    this.$vs.loading();
    document.body.classList.add("sds-page");
    await this.setRouteData();
    setTimeout(() => {
      this.$vs.loading.close();
    }, 500);
  },

  created() {
    this.$validator.extend("phoneFormat", {
      getMessage: () => "The phone number format is invalid.",
      validate: (value) =>
        value.match(
          /^(^04\(?\d{2}\)?[\s.-]\d{3}[\s.-]\d{3})|(^1300[\s.-]\d{3}[\s.-]\d{3})|(^1800[\s.-]\d{3}[\s.-]\d{3})|(^13[\s.-]\d{2}[\s.-]\d{2}$)|(^02[\s.-]\d{4}[\s.-]\d{4})|(^03[\s.-]\d{4}[\s.-]\d{4})|(^07[\s.-]\d{4}[\s.-]\d{4})|(^08[\s.-]\d{4}[\s.-]\d{4})/
        ) !== null,
    });
  },

  methods: {
    ...mapActions("paymentRequest", ["storeDirectOrderSdsPr"]),

    async setRouteData() {
      this.agencyEmail = (this.$route.query && this.$route.query.agencyEmail) ? this.$route.query.agencyEmail : "";
      this.agencyName = (this.$route.query && this.$route.query.agencyName) ? this.$route.query.agencyName : "";
      this.agencyPhone = (this.$route.query && this.$route.query.agencyPhone) ? this.$route.query.agencyPhone : "";
      this.file = (this.$route.query && this.$route.query.salesAuthority) ? this.$route.query.salesAuthority : "";
      this.logo = (this.$route.query && this.$route.query.logo) ? this.$route.query.logo : "";
      this.merchantId = (this.$route.query && this.$route.query.merchantId) ? this.$route.query.merchantId : "";
      this.source = (this.$route.query && this.$route.query.source) ? this.$route.query.source : "";
      this.vendorEmail = (this.$route.query && this.$route.query.vendorEmail) ? this.$route.query.vendorEmail : "";
      this.vendorName = (this.$route.query && this.$route.query.vendorName) ? this.$route.query.vendorName : "";
      this.vendorOnly = (this.$route.query && this.$route.query.vendorOnly && this.$route.query.vendorOnly == "true") ? true : false;
      this.vendorPhone = (this.$route.query && this.$route.query.vendorPhone) ? this.$route.query.vendorPhone : "";

      const cookieString = document.cookie;

      if (cookieString) {
        const match = cookieString.match(/(?:^|;\s*)directOrderData=([^;]*)/);

        if (match) {
          this.hasCookie = true;

          try {
            const directOrderData = JSON.parse(decodeURIComponent(match[1]));

            this.agencyEmail = directOrderData.agencyEmail || this.agencyEmail;
            this.agencyName = directOrderData.agencyName || this.agencyName;
            this.agencyPhone = directOrderData.agencyPhone || this.agencyPhone;
            this.file = directOrderData.salesAuthority || this.file;
            this.logo = directOrderData.logo || this.logo;
            this.source = directOrderData.source || this.source;
            this.vendorEmail = directOrderData.vendorEmail || this.vendorEmail;
            this.vendorName = directOrderData.vendorName || this.vendorName;
            this.vendorOnly = (directOrderData.vendorOnly && directOrderData.vendorOnly == "true") ? true : this.vendorOnly;
            this.vendorPhone = directOrderData.vendorPhone || this.vendorPhone;
          } catch (ex) {
            this.showMessage("Error", "Invalid direct order data", "danger", "icon-times");
          }
        }
      }

      this.emailNotify = !this.vendorOnly;

      if (this.file) {
        await this.downloadFileFromUrl();
      }

      this.dataLoaded = true;
    },

    async setSdsSettings(data) {
      this.sdsSettings = data;
      await this.$refs.sellerDisclosureStatementFields.setDefaults(); // auto select required items to order
    },

    setSdsDetails(data) {
      this.sdsOrderItems = data.sdsOrderItems;
      this.totalAmount = data.totalAmount;
    },

    getAddressData: function (addressData, placeResultData) {
      let addressField = { ...this.sdsAddress };

      if (placeResultData) {
        addressField.rawAddress = placeResultData.formatted_address;
      }

      if (addressData.street_number) {
        addressField.street_number = addressData.street_number;
      }

      if (addressData.postal_code) {
        addressField.postcode = addressData.postal_code;
      }

      if (addressData.locality) {
        addressField.suburb = addressData.locality;
      }

      if (addressData.administrative_area_level_1) {
        addressField.state = addressData.administrative_area_level_1;
      }

      if (addressData.route) {
        let street_address = "";

        for (const address of placeResultData.address_components) {
          if (address.types.includes("street_number")) {
            street_address += address.short_name + " ";
          }

          if (address.types.includes("route")) {
            street_address += address.long_name;
          }
        }

        addressField.street_name = street_address;
      }

      if (addressData.country) {
        addressField.country = addressData.country;
      }

      this.sdsAddress = addressField;
      this.clearAddressError();
    },

    clearAddressError() {
      if (this.errors.has("sdsAddress")) {
        this.errors.remove("sdsAddress");
      }
    },

    handleClearAddress() {
      this.sdsAddress = {
        country: "",
        postcode: "",
        rawAddress: "",
        state: "",
        street_name: "",
        street_number: "",
        suburb: "",
        unit_number: "",
      };

      this.$forceUpdate();
    },

    toggleExpand() {
      this.isAddressExpanded = !this.isAddressExpanded;
      this.handleClearAddress();
    },

    addressChange(event) {
      if (event && event.target.value) {
        this.clearAddressError();
      } else {
        this.handleClearAddress();
      }
    },

    setAttributeAutocomplete() {
      const autocompleteInput = document.getElementById("map");
      autocompleteInput.setAttribute("autocomplete", "new-password");
    },

    updateAddress(val) {
      this.getRawAddress();
      this.clearAddressError();
    },

    getRawAddress() {
      let address = "";
      let addressData = { ...this.sdsAddress };

      if (addressData.unit_number && Object.keys(addressData.unit_number).length > 0) {
        address = this.trimData(addressData.unit_number) + " ";
      }

      if (addressData.street_name && Object.keys(addressData.street_name).length > 0) {
        const streetNumber = this.getNumberFromString(addressData.street_name);

        if (streetNumber) {
          this.sdsAddress.street_number = streetNumber;
        }

        address = address + this.trimData(addressData.street_name) + ", ";
      } else if (addressData.street && Object.keys(addressData.street).length > 0) {
        address = address + this.trimData(addressData.street) + ", ";
      }

      if (addressData.suburb && Object.keys(addressData.suburb).length > 0) {
        address = address + this.trimData(addressData.suburb) + " ";
      }

      if (addressData.state && Object.keys(addressData.state).length > 0) {
        address = address + this.trimData(addressData.state);
        if (Object.keys(addressData.postcode).length > 0) {
          address = address + " " + this.trimData(addressData.postcode) + ", ";
        } else {
          address = address + ", ";
        }
      }

      if (addressData.country && Object.keys(addressData.country).length > 0) {
        address = address + this.trimData(addressData.country);
      } else if (Object.keys(address).length) {
        this.sdsAddress.country = "Australia";
        address = address + "Australia";
      }

      this.sdsAddress.rawAddress = this.trimData(address);

      return address;
    },

    trimData(data) {
      if (!data) {
        return;
      }

      return data.trim();
    },

    getNumberFromString(text) {
      const match = text.match(/^\d+/);
      const number = match ? match[0] : "";
      return number;
    },

    updateFile(input) {
      if (input.target.files && input.target.files[0]) {
        this.sdsSalesAuthority = input.target.files[0];
      }
    },

    clearFilePreview() {
      this.sdsSalesAuthority = "";
      this.$refs.sdsSalesAuthorityRef.value = "";
      this.fileLoadedFromUrl = false;

      if (this.errors.has("salesAuthorityFile")) {
        this.errors.remove("salesAuthorityFile");
      }
    },

    async createPr() {
      try {
        await this.validateSdsForm();
        const valid = await this.$validator.validateAll();

        if (!valid || this.disableCreateButton) {
          return false;
        }

        const agency = {
          email: this.agencyEmail,
          name: this.agencyName,
          notificationEmail: this.agencyNotificationEmail,
          phone: this.agencyPhone.split(" ").join(""),
          vendorOnly: this.vendorOnly
        };

        const formData = new FormData();
        formData.append("agency", JSON.stringify(agency));
        formData.append("payeeEmail", this.vendorEmail);
        formData.append("payeePhone", this.vendorPhone.split(" ").join(""));
        formData.append("payeeName", this.vendorName);
        formData.append("sdsAddress", JSON.stringify(this.sdsAddress));
        formData.append("sdsOrderItems", JSON.stringify(this.sdsOrderItems));
        formData.append("file", this.sdsSalesAuthority);
        formData.append("sourceCrm", this.source);

        if (this.merchantId) {
          formData.append("merchantId", this.merchantId);
        }

        let obj = {
          obj: formData,
          config: {
            header: {
              "Content-Type": "application/json",
            },
          },
        };

        this.$vs.loading();

        await this.storeDirectOrderSdsPr(obj).then((result) => {
          this.$vs.loading.close();
          this.showMessage("Success", "Request ordered successfully.", "success", "icon-check-circle");
          this.paymentRequest = result.data.data;
          this.showSuccessBox = true;

          if (this.hasCookie) {
            document.cookie = "directOrderData=; Max-Age=0; path=/";
          }
        }).catch((ex) => {
          this.$vs.loading.close();
          this.showMessage("Error", ex.data.message, "danger", "icon-times");
        });
      } catch (ex) {
        this.$vs.loading.close();
      }
    },

    async validateSdsForm() {
      if (!this.sdsAddress.rawAddress || !this.sdsAddress.street_name || !this.sdsAddress.state || !this.sdsAddress.postcode) {
        this.errors.add({
          field: "sdsAddress",
          msg: "Address field is required"
        });
      }

      if (!this.sdsOrderItems || !this.sdsOrderItems.length) {
        this.errors.add({
          field: "sdsOrderItems",
          msg: "Please select items to order"
        });
      }
    },

    showMessage(title, message, color, icon) {
      this.$vs.notify({
        title: title,
        text: message,
        color: color,
        iconPack: "feather",
        position: "top-right",
        icon: icon,
      });
    },

    setNotificationEmail() {
      if (this.emailNotify) {
        this.agencyNotificationEmail = this.agencyEmail;
      }
    },

    async downloadFileFromUrl() {
      try {
        const fileUrl = encodeURIComponent(this.file);
        const proxyUrl = `${process.env.VUE_APP_API_URL}api/v1/admin/upload/download-file?url=${fileUrl}`;
        const response = await fetch(proxyUrl);

        if (!response.ok) {
          this.showMessage("Error", "Failed to attach provided sales authority file", "danger", "icon-times");
          return false;
        }

        const contentType = response.headers.get("Content-Type");

        if (contentType != "application/pdf") {
          this.showMessage("Error", "Provided sales authority file should be pdf", "danger", "icon-times");
          return false;
        }

        const blob = await response.blob();
        const fileSize = blob.size;

        // checking file size in bytes
        if (fileSize > (this.maxFileSize * 1024 * 2014)) {
          this.showMessage("Error", `Provided sales authority file should be less than ${this.maxFileSize} MB`, "danger", "icon-times");
          return false;
        }

        let filename = "sales-authority.pdf";
        const contentDisposition = response.headers.get("Content-Disposition");

        if (contentDisposition) {
          const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);

          if (match != null && match[1]) {
            filename = match[1].replace(/['"]/g, '');
          }
        }

        this.sdsSalesAuthority = new File([blob], filename, { type: blob.type });
        this.fileLoadedFromUrl = true;
      } catch (ex) {
        this.showMessage("Error", "Failed to attach provided sales authority file", "danger", "icon-times");
      }
    }
  },

  watch: {
    emailNotify(val) {
      this.agencyNotificationEmail = (val && this.agencyEmail) ? this.agencyEmail : "";
    },

    sdsOrderItems(val) {
      if (val && val.length && this.errors.has("sdsOrderItems")) {
        this.errors.remove("sdsOrderItems");
      } else if ((!val || !val.length) && !this.errors.has("sdsOrderItems")) {
        this.errors.add({
          field: "sdsOrderItems",
          msg: "Please select items to order"
        });
      }
    }
  },

  computed: {
    showFilePreview() {
      return (this.sdsSalesAuthority && typeof this.sdsSalesAuthority === "object");
    },

    checkPhoneNumber: function () {
      return function (field) {
        let mask = "#### ### ###";
        const formattedValue = field.replace(/\D/g, "");

        if (formattedValue && formattedValue.length > 0) {
          const numLength = formattedValue.length;
          let value1 = "";
          let value2 = "";

          if (numLength >= 2) {
            value1 = formattedValue.substring(0,2);

            if (value1 === "13") {
              value2 = formattedValue.substring(0, 4);
            }
          }

          if (["02", "03", "07", "08"].includes(value1)) {
            mask = "## #### ####";
          } else {
            mask = "#### ### ###";
          }

          if (value1 === "13") {
            if (value2 === "130" || value2 === "1300") {
              mask = "#### ### ###";
            } else {
              mask = "## ## ##";
            }
          }
        }

        return mask;
      }
    },

    disableCreateButton() {
      return (this.errors.items.length > 0);
    },
  }
}
</script>

<style scoped>
.con-vs-checkbox {
  display: flex !important;
  align-items: flex-start !important;
}
</style>